import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useObras } from '@/hooks/useColadas';
import { cn } from '@/lib/utils';
import type { Obra } from '@adelante/shared';
import { Check, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * Combobox de obras (con búsqueda + toggle "solo activas").
 *
 * No es un control fully-floating (sin Popover/Command de shadcn —
 * preferimos algo más simple integrado en línea). Funciona como una
 * sección dentro del modal padre con:
 *  - Input de búsqueda.
 *  - Toggle "Solo activas / Todas".
 *  - Lista scrolleable de resultados (filtrados por backend).
 *  - Item seleccionado destacado con check.
 *  - Botón "Quitar selección" (opcional).
 */

interface Props {
  /** Works_no seleccionado, o null si no hay nada elegido. */
  valor: string | null;
  onChange: (workNo: string | null) => void;
  /** Permitir quitar la selección (default true). */
  permitirNulo?: boolean;
  /** Máx alto de la lista en px (default 280). */
  maxAltoPx?: number;
}

export function PickerObras({ valor, onChange, permitirNulo = true, maxAltoPx = 280 }: Props) {
  const [q, setQ] = useState('');
  const [soloActivas, setSoloActivas] = useState(true);

  const filtros = useMemo(
    () => ({
      q: q.trim() || undefined,
      solo_activas: soloActivas,
      limite: 500,
    }),
    [q, soloActivas],
  );

  const consulta = useObras(filtros);
  const obras: Obra[] = consulta.data?.data?.obras ?? [];

  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label htmlFor="picker-obras-q">Buscar obra</Label>
        <div className="relative">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="picker-obras-q"
            placeholder="Código, nombre, descripción..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={soloActivas}
            onChange={(e) => setSoloActivas(e.target.checked)}
            className="h-4 w-4"
          />
          Solo obras activas
        </label>
        <span className="text-muted-foreground">
          {consulta.isLoading ? 'Cargando…' : `${obras.length} resultado(s)`}
        </span>
      </div>

      <div className="overflow-y-auto rounded-md border" style={{ maxHeight: `${maxAltoPx}px` }}>
        {consulta.error && (
          <p className="p-3 text-sm text-destructive">
            Error al cargar obras: {consulta.error.message}
          </p>
        )}
        {!consulta.isLoading && !consulta.error && obras.length === 0 && (
          <p className="p-3 text-center text-sm text-muted-foreground">
            No hay obras que coincidan con la búsqueda.
          </p>
        )}
        {obras.map((obra) => {
          const seleccionada = obra.works_no === valor;
          return (
            <button
              key={obra.works_no}
              type="button"
              onClick={() => onChange(obra.works_no)}
              className={cn(
                'flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-zinc-50',
                seleccionada && 'bg-emerald-50 hover:bg-emerald-50',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{obra.works_no}</span>
                  {obra.status && obra.status !== 'Open' && (
                    <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600">
                      {obra.status}
                    </span>
                  )}
                </div>
                <div className="truncate text-sm">
                  {obra.display_name ?? obra.description ?? '—'}
                </div>
              </div>
              {seleccionada && <Check className="h-4 w-4 flex-none text-emerald-600" />}
            </button>
          );
        })}
      </div>

      {permitirNulo && valor !== null && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
          className="justify-self-start gap-1.5"
        >
          <X className="h-4 w-4" /> Quitar selección
        </Button>
      )}
    </div>
  );
}
